import { isIE } from "react-device-detect";
import { IntlProvider } from "react-intl";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import Head from "next/head";
import Script from "next/script";
import * as gtag from "../lib/gtag";
import BrowserNotSupported from "../components/BrowserNotSupported";
import DialogScript from "../components/js/DialogScript";

// Style
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

export const flattenMessages = (nestedMessages, prefix = "") => {
	if (nestedMessages === null) return {};
	return Object.keys(nestedMessages).reduce((messages, key) => {
		const value = nestedMessages[key];
		const prefixedKey = prefix ? `${prefix}.${key}` : key;
		if (typeof value === "string") {
			Object.assign(messages, { [prefixedKey]: value });
		} else {
			Object.assign(messages, flattenMessages(value, prefixedKey));
		}
		return messages;
	}, {});
};

const languages = {
	pl: flattenMessages(require("../locale/pl.json")),
};

const organizationSchema = {
	"@context": "https://schema.org",
	"@type": "Organization",
	"@id": "https://przyjazne-deklaracje.pl/#organization",
	name: "Przyjazne Deklaracje",
	url: "https://przyjazne-deklaracje.pl/",
	logo: {
		"@type": "ImageObject",
		url: "https://przyjazne-deklaracje.pl/resources/images/logo/przyjazne-deklaracje/logo-desktop.svg",
	},
	sameAs: [
		"https://www.facebook.com/PrzyjazneDeklaracje/",
		"https://www.instagram.com/przyjazne_deklaracje/",
		"https://www.youtube.com/channel/UC4PoPhe0YqKX_Z8uT01dHxw",
	],
};

const websiteSchema = {
	"@context": "https://schema.org",
	"@type": "WebSite",
	"@id": "https://przyjazne-deklaracje.pl/#website",
	name: "Przyjazne Deklaracje",
	url: "https://przyjazne-deklaracje.pl/",
	publisher: {
		"@id": "https://przyjazne-deklaracje.pl/#organization",
	},
	inLanguage: "pl-PL",
};

export default function App({ Component, pageProps }) {
	const router = useRouter();
	const { locale, defaultLocale } = router;
	const messages = languages[locale];
	const [isIeBrowser, setIsIeBrowser] = useState(false);

	useEffect(() => {
		if (isIE) setIsIeBrowser(true);

		const handleRouteChange = (url) => {
			gtag.pageview(url);
		};

		gtag.pageview(window.location.pathname);

		router.events.on("routeChangeComplete", handleRouteChange);
		return () => {
			router.events.off("routeChangeComplete", handleRouteChange);
		};
	}, [router.events]);

	if (isIeBrowser) return <BrowserNotSupported />;

	return (
		<>
			<Head>
				<meta name="viewport" content="width=device-width, initial-scale=1.0" />

				<script
					id="organization-schema"
					type="application/ld+json"
					dangerouslySetInnerHTML={{
						__html: JSON.stringify(organizationSchema),
					}}
				/>

				<script
					id="website-schema"
					type="application/ld+json"
					dangerouslySetInnerHTML={{
						__html: JSON.stringify(websiteSchema),
					}}
				/>
			</Head>

			<Script
				id="gtm-init"
				strategy="beforeInteractive"
				dangerouslySetInnerHTML={{
					__html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
          `,
				}}
			/>

			<Script
				id="gtm-script"
				strategy="afterInteractive"
				dangerouslySetInnerHTML={{
					__html: `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${gtag.GTM_ID}');
          `,
				}}
			/>

			<Script
				src="https://przyjazne-deklaracje.pl/resources/js/general/jquery.min.js"
				strategy="beforeInteractive"
			/>

			<Script
				src="https://unpkg.com/default-passive-events"
				strategy="beforeInteractive"
			/>

			<IntlProvider
				messages={messages}
				locale={locale}
				defaultLocale={defaultLocale}
			>
				<DialogScript />
				<Component {...pageProps} />
			</IntlProvider>
		</>
	);
}